import { Link } from "react-router-dom";
import { COMPANY } from "../../data/company";
import SEO from "../../components/SEO";
import ServiceGallerySection from "../../components/ServiceGallerySection";
import ContactForm from "../../components/ContactForm";
import { imageUrl } from "../../lib/images";

export default function IndoorRenovation() {
  return (
    <>
      <SEO
        title="Indendørs renovering – lofter & vægge"
        description={`Indendørs renovering, lofter og vægge udført af ${COMPANY.name} i ${COMPANY.city} på Fyn. Akustiklofter, gipslofter og ombygning. Ring ${COMPANY.phone}.`}
        path="/ydelser/indendoers-renovering"
        image="/_assets/indendoers-renovering-lofter-vaegge.jpg"
      />

      {/* Hero header */}
      <section className="bg-[#111111] text-white py-16 sm:py-24 border-b border-neutral-800 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/indendoers-renovering-lofter-vaegge.jpg", "width=1920,quality=80,format=auto")}
            alt="Indendørs tømrerrenovering med lofter og vægge"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/95 to-[#111111]/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-3">
              <Link to="/" className="hover:underline">Forside</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <Link to="/ydelser" className="hover:underline">Ydelser</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <span>Indendørs renovering</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Indendørs renovering: Lofter og vægge
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-300 leading-relaxed">
              Trænger boligen til fornyelse, bedre rumfordeling eller roligere akustik? Hos <strong>{COMPANY.name}</strong> udfører vi indendørs tømrerrenovering med særligt fokus på lofter, vægge og gennemført detaljearbejde.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={COMPANY.phoneLink}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[#A78E51] text-white font-extrabold text-sm hover:bg-[#937B43] transition-colors"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Ring direkte: {COMPANY.phone}</span>
              </a>
              <a
                href="#kontakt-sektion"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-neutral-600 transition-colors"
              >
                <span>Få tilbud på renovering</span>
                <i className="fa-solid fa-arrow-down text-xs text-[#A78E51]" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Details */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-neutral-700 leading-relaxed">
              <div className="inline-block px-3 py-1 bg-amber-50 text-[#A78E51] rounded text-xs font-bold uppercase tracking-wider">
                Indvendig modernisering
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111]">
                Nye lofter og vægge der forvandler din bolig
              </h2>
              <p>
                Et nyt loft eller en ændret vægopbygning kan ændre et rums udtryk totalt. Vi hjælper boligejere med at skabe lyse, moderne og behagelige rammer i hverdagen. Uanset om det gælder opsætning af moderne akustiklofter med varme trælameller, glatte gipslofter med indbyggede spots eller nye skillevægge til opdeling af værelser, leverer vi et resultat i absolut særklasse.
              </p>
              <p>
                Vi sørger for omhyggelig opretning af eksisterende skæve underlag, korrekt isolering mod lyd og kulde samt pæne samlinger mod lister, gerigter og dørpartier. Vi efterlader altid arbejdsområdet ordentligt, så processen føles tryg fra første dag til det færdige rum.
              </p>

              {/* Bullet Points */}
              <div className="pt-4 space-y-3">
                <h3 className="font-bold text-lg text-[#111111]">
                  Vores indendørs renoveringsarbejde omfatter:
                </h3>
                <ul className="space-y-2.5">
                  {[
                    "Opsætning af akustiklofter og akustikpaneler i eg, valnød eller ask",
                    "Montering af glatte gipslofter og lofter med troldtekt eller træpaneler",
                    "Etablering af nye lette skillevægge med effektiv lydisolering",
                    "Nedtagning eller flytning af ikke-bærende vægge for mere åbne rum",
                    "Isolering af lofter, etageadskillelser og indvendige vægkonstruktioner",
                    "Montering af fodlister, fejelister, gerigter og indvendige døre",
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-[#A78E51] shrink-0 mt-0.5">
                        <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                      </div>
                      <span className="text-neutral-800 text-sm sm:text-base font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Card / Fast facts */}
            <div className="lg:col-span-5 bg-neutral-50 rounded-xl p-8 border border-neutral-200 shadow-sm space-y-6">
              <h3 className="text-xl font-bold text-[#111111] border-b border-neutral-200 pb-4">
                Hvorfor vælge os til indendørs renovering?
              </h3>

              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-volume-xmark" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#111111] text-sm">Bedre akustik & indeklima</h4>
                    <p className="text-xs text-neutral-600 mt-1">
                      Akustikpaneler eller Troldtekt dæmper rumklang markant i store køkken-alrum og stuer.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-ruler-horizontal" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#111111] text-sm">Lige linjer og grundig opretning</h4>
                    <p className="text-xs text-neutral-600 mt-1">
                      Gamle boliger har sjældent snorlige lofter. Vi retter underlaget grundigt op for et plant finish.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-broom" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#111111] text-sm">Hensyn og ryddelighed</h4>
                    <p className="text-xs text-neutral-600 mt-1">
                      Vi ved, at indendørs arbejde foregår midt i dit hjem. Vi dækker pænt af og rydder ordentligt op hver dag.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-neutral-200">
                <div className="bg-white p-4 rounded-lg border border-neutral-200 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-neutral-500 block">Kontakt tømrermesteren</span>
                    <span className="font-bold text-sm text-[#111111]">{COMPANY.name}</span>
                  </div>
                  <a
                    href={COMPANY.phoneLink}
                    className="px-3.5 py-2 bg-[#A78E51] hover:bg-[#937B43] text-white text-xs font-bold rounded transition-colors"
                  >
                    Ring op
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery linked to CMS slug 'indendoers-renovering' */}
      <ServiceGallerySection
        gallerySlug="indendoers-renovering"
        fallbackTitle="Indendørs renovering – lofter & vægge"
        subtitle="Billeder af udførte akustiklofter, gipsvægge og indvendigt tømrerarbejde"
        fallbackImages={[
          "/_assets/indendoers-renovering-lofter-vaegge.jpg",
          "/_assets/indendoers-toemrer-akustikloft-vaegge.jpg",
        ]}
      />

      {/* Contact Form Section */}
      <section id="kontakt-sektion" className="py-16 sm:py-20 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-[#A78E51] text-xs font-bold uppercase tracking-widest block mb-2">
              Få et uforpligtende tilbud
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
              Klar til at forny lofter og vægge?
            </h2>
            <p className="mt-3 text-neutral-400 text-sm sm:text-base">
              Fortæl os om dit renoveringsprojekt, så vender vi hurtigt tilbage med vejledning og et gennemskueligt tilbud.
            </p>
          </div>

          <div className="bg-white text-neutral-900 rounded-xl p-6 sm:p-10 shadow-xl">
            <ContactForm
              defaultService="Indendørs renovering"
              heading="Forespørgsel på indendørs renovering"
            />
          </div>
        </div>
      </section>
    </>
  );
}
